import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { OperationDetailOverlayComponent } from '@components/operation-detail-overlay/operation-detail-overlay.component';
import { fadeInOut } from '@helpers/fadeInOut';
import { CssVarService } from '@service/css-vars.service';
import { OperationsHistoryService } from '@service/ops-operations-history-services/operations-history-service.service';
import { PaginationStrategy } from '@service/pagination-strategy.service';
import { ResponsiveService } from '@service/responsive.service';
import { StaticPaginationStrategy } from '@service/static-pagination-strategy';
import dayjs from 'dayjs';
import { OperationsHistoryData } from 'ops/common/models/operations-history-data';
import { Subject } from 'rxjs';
import { DynamicMaxHeightDirective } from 'src/app/directives/alarm-table-height/alarm-table-height.directive';
import { OpsAlarmChild } from '../ops-alarm.component';

const DEFAULT_ITEMS_PER_PAGE = 6;
@Component({
    selector: 'app-ops-operations',
    templateUrl: './ops-operations.component.html',
    styleUrls: ['./ops-operations.component.scss'],
    animations: [fadeInOut],
    providers: [
        {
            provide: PaginationStrategy,
            useValue: new StaticPaginationStrategy(DEFAULT_ITEMS_PER_PAGE),
        },
    ],
    standalone: false
})
export class OpsOperationsComponent implements OnInit {
    @ViewChild('dynamicMaxHeight') dynamicMaxHeight: DynamicMaxHeightDirective;
    @ViewChild('filters') filters: ElementRef;
    @ViewChild('oprationDetailOverlay') oprationDetailOverlay: OperationDetailOverlayComponent;
    @ViewChild('tableContent') tableContent: ElementRef;
    @ViewChild('resetFilterButton') resetFilterButton: ElementRef;
    @Output() onBackEmit = new EventEmitter();
    @Output() onBackToTop = new EventEmitter();

    readonly pageTitle = OpsAlarmChild.Operations;

    protected _currentPage = 0;
    private _isLoading = false;
    private operationsHistoryArray: OperationsHistoryData[] = [];

    operationDetail: OperationsHistoryData;
    clickRefreshSubject: Subject<boolean> = new Subject();
    filterOption: FilterOptions = {
        datetime: '',
        status: AlarmLevels.UNDEFINED,
        channel: '',
    };
    savedFilters: FilterOptions | null = null;
    displayResetOption: boolean = false;
    filterOptionTemp = {};
    showSearchDivider: boolean = true;
    filtredOperationsData: OperationsHistoryData[] = [];
    removeHeightLimits: boolean = false;

    constructor(
        private operationHistoryService: OperationsHistoryService,
        public responsive: ResponsiveService,
        protected paginationStrategy: PaginationStrategy,
        private cssVarService: CssVarService,
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.shouldRemoveHeightLimit();
    }

    @HostListener('document:click', ['$event'])
    handleClick(event: Event) {
        if (
            this.displayResetOption &&
            !this.resetFilterButton.nativeElement.contains(event.target)
        ) {
            this.displayResetOption = false;
        }
    }

    async ngOnInit() {
        this.operationsHistoryArray =
            await this.operationHistoryService.getOperationsHistoryDataAPI();
        this.dataFilter();
        this.paginationStrategy.recalculate(this.operationsHistoryArray.length);
        this.shouldRemoveHeightLimit();
    }

    ngAfterViewInit(): void {
        const tableRowHeightAlarm = this.cssVarService.tableRowHeightAlarm;
        const tableRowGap = this.cssVarService.tableRowGap;
        this.dynamicMaxHeight.init(
            tableRowHeightAlarm,
            tableRowGap,
            DEFAULT_ITEMS_PER_PAGE,
            this.filters.nativeElement.offsetHeight,
        );
    }

    async onClickRefresh() {
        this.filtredOperationsData = [];
        this.operationsHistoryArray =
            await this.operationHistoryService.getOperationsHistoryDataAPI();
        this.dataFilter();
        this.calculatePagination();
    }

    resetFilter() {
        this.saveFilters();
        const keys = Object.keys(this.filterOption);
        for (let i = 0; i < keys.length; i++) {
            if (this.filterOption[keys[i]] !== '') {
                if (!this.savedFilters) {
                    this.saveFilters();
                }
                this.filterOption[keys[i]] = '';
            }
        }
        this.dataFilter();
    }

    refreshFilters() {
        this.displayResetOption = false;
        this.filterOption = {
            ...this.savedFilters,
        };
        this.savedFilters = null;
    }

    onSearchClick() {
        this.displayResetOption = false;
    }

    saveFilters() {
        this.displayResetOption = true;
        this.savedFilters = {
            ...this.filterOption,
        };
    }

    isClearFilterVisible() {
        if (this.displayResetOption) {
            return false;
        }

        let hasFilterOptions = false;
        const values = Object.values(this.filterOption);
        for (let i = 0; i < values.length; i++) {
            if (values[i] !== '') {
                hasFilterOptions = true;
            }
        }
        return hasFilterOptions;
    }

    onBack() {
        this.onBackEmit.emit();
    }

    public dataFilter() {
        let newOperationsData = this.operationsHistoryArray;

        newOperationsData = newOperationsData.filter((data: OperationsHistoryData) =>
            this.filterOption.datetime !== ''
                ? dayjs(new Date(data.datetime)).format('YYYY/MM/DD') === this.filterOption.datetime
                : data,
        );

        newOperationsData = newOperationsData.filter((data: OperationsHistoryData) =>
            this.filterOption.status !== ''
                ? data.status === this.filterOption.status.toUpperCase()
                : data,
        );

        newOperationsData = newOperationsData.filter((data: OperationsHistoryData) =>
            this.filterOption.channel !== ''
                ? data.id === this.filterOption.channel ||
                  data.name === this.filterOption.channel.toUpperCase()
                : data,
        );

        this.filtredOperationsData = newOperationsData;
        this.calculatePagination();
    }

    calculatePagination() {
        this.paginationStrategy.recalculate(this.filtredOperationsData.length);
    }

    handleCalendarDate(event: { name: CalendarOptions; value: string }) {
        if (event.name == CalendarOptions.DATETIME) {
            this.filterOption.datetime = event.value;
        }
    }

    displayedPageLimitCount() {
        if (this.responsive.isLarge() || this.responsive.isXLarge()) {
            return 7;
        } else {
            return 5;
        }
    }

    onSearchInputChange() {
        if (!this.filterOption.channel.trim()) {
            this.onSearchClick();
            this.dataFilter();
        }
    }

    onDetail(operation: OperationsHistoryData) {
        this.operationDetail = operation;
        this.oprationDetailOverlay.setVisible(true);
    }

    onScroll() {
        if (this.responsive.isXSmall() || this.responsive.isHandsetLandscape()) {
            const { scrollTop, scrollHeight, clientHeight } = this.tableContent.nativeElement;
            if (scrollTop + clientHeight >= scrollHeight - 5 && !this.isLoading) {
                this.loadMoreItems();
            }
        }
    }

    loadMoreItems() {
        this._currentPage = Math.min(this._currentPage + 1, this.pageCount);
    }

    onTop() {
        this.onBackToTop.emit()
    }

    isScrollBtnVisible() {
        if (this.tableContent) {
            const rect = this.tableContent.nativeElement.getBoundingClientRect();
            const topShown = rect.top < 50;

            return topShown;
        }
        return false;
    }

    shouldRemoveHeightLimit() {
        if (window.innerHeight < 700) {
            this.removeHeightLimits = true;
        } else {
            this.removeHeightLimits = false;
        }
    }

    public get pageStart() {
        return !this.responsive.isXSmall() && !this.responsive.isHandsetLandscape()
            ? this.paginationStrategy.getPageStart(this._currentPage)
            : 0;
    }
    public get pageEnd() {
        return this.paginationStrategy.getPageEnd(this._currentPage);
    }

    public get pageCount() {
        return this.paginationStrategy.getPageCount();
    }

    public get isLoading() {
        return this._isLoading;
    }

    public get currentPage() {
        return this._currentPage;
    }
    public set currentPage(value: number) {
        this._currentPage = value;
    }
}

export enum AlarmLevels {
    LOW = 'LOW',
    LOWLOW = 'LOW-LOW',
    HIGHHIGH = 'HIGH-HIGH',
    HIGH = 'HIGH',
    FAIL = 'FAIL',
    ABNORMAL = 'ABNORMAL',
    UNDEFINED = '',
}

export interface FilterOptions {
    status: AlarmLevels;
    channel: string;
    datetime: string;
}

export enum CalendarOptions {
    DATETIME = 'datetime',
}
